import { useAuth } from "@dl/web-core-js/auth";
import { Badge, Button, Field, Input, Segmented, Select, Textarea, toast } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  formatNumber,
  refusalMessage,
  StockAdjustmentReasonClient,
  StockBalanceClient,
  StockClient,
  type PartGridDTO,
  type StockAdjustmentMode,
  type StockAdjustmentResult,
} from "../../api/manufacturing";
import { WarehouseClient } from "../../api/product";
import { PartPicker } from "./PartPicker";

interface Line {
  part_id: number;
  number: string;
  name: string;
  unit: string | null;
  quantity: string;
  note: string;
}

/** Enough of a part to start a line with. */
export interface AdjustablePart {
  id: number;
  number: string;
  name: string;
  stock_unit_code: string | null;
}

/**
 * Today in the browser's own time zone, as yyyy-mm-dd.
 *
 * <p>Not {@code toISOString()}: that is UTC, so from 20:00 in Montréal a form
 * opened in the evening would default to tomorrow.</p>
 */
export function localToday(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

/**
 * The corrective tool: post a manual adjustment or a count.
 *
 * <p>Always additive. In DELTA mode each quantity is posted as typed ("+5",
 * "-2"). In COUNTED mode it is what is on the shelf, and the server posts the
 * difference from the balance at the moment Save is pressed — not the one on
 * screen — so a receipt landing in between is counted rather than overwritten.
 * The preview here uses the balance as loaded, and says so.</p>
 *
 * <p>A reason is required, because "why is this part 400 short" should always
 * have an answer. Distribution warehouses are not offered: their stock lives in
 * nexera, not in this ledger.</p>
 */
export function StockAdjustmentForm({
  initialParts = [],
  initialWarehouseId,
  onPosted,
}: {
  initialParts?: AdjustablePart[];
  initialWarehouseId?: number;
  onPosted?: (result: StockAdjustmentResult) => void;
}) {
  const { t } = useTranslation();
  const { http, canWrite } = useAuth();
  const queryClient = useQueryClient();

  const stock = useMemo(() => new StockClient(http), [http]);
  const balances = useMemo(() => new StockBalanceClient(http), [http]);

  const warehouses = useQuery({
    queryKey: ["warehouse", "all"],
    queryFn: () => new WarehouseClient(http).findAll(),
    staleTime: 30 * 60_000,
  });
  const reasons = useQuery({
    queryKey: ["stock-adjustment-reason", "all"],
    queryFn: () => new StockAdjustmentReasonClient(http).findAll(),
    staleTime: 30 * 60_000,
  });

  const plantWarehouses = (warehouses.data ?? []).filter((w) => w.is_active && w.kind !== "DISTRIBUTION");
  const defaultWarehouse =
    initialWarehouseId ?? plantWarehouses.find((w) => w.kind === "MANUFACTURING")?.id ?? plantWarehouses[0]?.id;

  const [warehouseId, setWarehouseId] = useState<number | undefined>(initialWarehouseId);
  const effectiveWarehouse = warehouseId ?? defaultWarehouse;
  const [reasonId, setReasonId] = useState<number | undefined>(undefined);
  const [mode, setMode] = useState<StockAdjustmentMode>("DELTA");
  const [date, setDate] = useState(localToday);
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [lines, setLines] = useState<Line[]>(
    initialParts.map((part) => ({
      part_id: part.id,
      number: part.number,
      name: part.name,
      unit: part.stock_unit_code,
      quantity: "",
      note: "",
    })),
  );
  const [result, setResult] = useState<StockAdjustmentResult | null>(null);

  const partIds = lines.map((line) => line.part_id);
  const current = useQuery({
    queryKey: ["manufacturing", "stock-balance", "for-adjustment", effectiveWarehouse, [...partIds].sort()],
    queryFn: () =>
      balances.find({
        page: 0,
        size: 500,
        filters: { warehouse_id: [String(effectiveWarehouse)], part_id: partIds.map(String) },
      }),
    enabled: effectiveWarehouse !== undefined && partIds.length > 0,
  });
  const balanceOf = (partId: number): number =>
    Number((current.data?.content ?? []).find((balance) => balance.part_id === partId)?.quantity ?? 0);

  const addPart = (part: PartGridDTO) =>
    setLines((existing) =>
      existing.some((line) => line.part_id === part.id)
        ? existing
        : [
            ...existing,
            { part_id: part.id, number: part.number, name: part.name, unit: part.stock_unit_code, quantity: "", note: "" },
          ],
    );

  const setLine = (partId: number, patch: Partial<Line>) =>
    setLines((existing) => existing.map((line) => (line.part_id === partId ? { ...line, ...patch } : line)));

  const parsed = (value: string) => (value.trim() === "" ? Number.NaN : Number(value));
  // Every bad line is marked, not only the first one found.
  const isInvalid = (line: Line) => {
    const quantity = parsed(line.quantity);
    return Number.isNaN(quantity) || (mode === "DELTA" && quantity === 0) || (mode === "COUNTED" && quantity < 0);
  };
  const invalidCount = lines.filter(isInvalid).length;
  const ready =
    canWrite && effectiveWarehouse !== undefined && reasonId !== undefined && lines.length > 0 && invalidCount === 0;
  // Say why Post is greyed out rather than leaving the user to guess.
  const blocker = !canWrite
    ? null
    : reasonId === undefined
      ? t("stock.adjust.need-reason", "Choose a reason.")
      : lines.length === 0
        ? t("stock.adjust.need-lines", "Add at least one part.")
        : invalidCount > 0
          ? mode === "DELTA"
            ? t("stock.adjust.need-delta", "Every line needs a change other than zero.")
            : t("stock.adjust.need-counted", "Every line needs a counted quantity of zero or more.")
          : null;

  const post = useMutation({
    mutationFn: () =>
      stock.adjust({
        warehouse_id: effectiveWarehouse!,
        stock_adjustment_reason_id: reasonId!,
        mode,
        document_date: date || null,
        reference: reference.trim() || null,
        note: note.trim() || null,
        lines: lines.map((line) => ({
          part_id: line.part_id,
          quantity: parsed(line.quantity),
          note: line.note.trim() || null,
        })),
      }),
    onSuccess: async (posted) => {
      setResult(posted);
      await queryClient.invalidateQueries({ queryKey: ["manufacturing", "stock-balance"] });
      await queryClient.invalidateQueries({ queryKey: ["manufacturing", "stock-movement"] });
      await queryClient.invalidateQueries({ queryKey: ["manufacturing", "part-grid"] });
      // The part page's header and the picker both show on-hand too.
      await queryClient.invalidateQueries({ queryKey: ["manufacturing", "part"] });
      await queryClient.invalidateQueries({ queryKey: ["manufacturing", "part-picker"] });
      toast.success(
        posted.movements_posted === 0
          ? t("stock.adjust.nothing", "Everything already matched; nothing was posted.")
          : t("stock.adjust.posted", "{{n}} movements posted", { n: posted.movements_posted }),
      );
      onPosted?.(posted);
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  if (result) {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-[13px] text-ink">
          {result.stock_document_id === null
            ? t("stock.adjust.nothing", "Everything already matched; nothing was posted.")
            : t("stock.adjust.done", "Document #{{id}} posted.", { id: result.stock_document_id })}
        </p>
        <table className="w-full text-[13px] tabular-nums">
          <thead className="text-left text-[11px] uppercase tracking-wide text-ink-faint">
            <tr>
              <th className="py-1">{t("column.part.number", "Number")}</th>
              <th className="py-1 text-right">{t("stock.before", "Before")}</th>
              <th className="py-1 text-right">{t("stock.delta", "Change")}</th>
              <th className="py-1 text-right">{t("stock.after", "After")}</th>
            </tr>
          </thead>
          <tbody>
            {result.lines.map((line) => (
              <tr key={line.part_id} className="border-t border-line">
                <td className="py-1">{line.part_number}</td>
                <td className="py-1 text-right">{formatNumber(line.balance_before)}</td>
                <td className={"py-1 text-right " + (line.delta > 0 ? "text-ok" : line.delta < 0 ? "text-risk" : "text-ink-faint")}>
                  {line.delta > 0 ? "+" : ""}
                  {formatNumber(line.delta)}
                </td>
                <td className="py-1 text-right font-medium">{formatNumber(line.balance_after)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div>
          <Button
            variant="ghost"
            onClick={() => {
              setResult(null);
              setLines([]);
              setReference("");
              setNote("");
            }}
          >
            {t("stock.adjust.another", "Post another")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Field label={t("form.field.warehouse", "Warehouse")} htmlFor="adj-warehouse" required>
          <Select
            id="adj-warehouse"
            value={effectiveWarehouse === undefined ? "" : String(effectiveWarehouse)}
            options={plantWarehouses.map((w) => ({ value: String(w.id), label: `${w.code} · ${w.name ?? ""}` }))}
            onChange={(event) => setWarehouseId(Number(event.target.value))}
          />
        </Field>
        <Field label={t("form.field.reason", "Reason")} htmlFor="adj-reason" required>
          <Select
            id="adj-reason"
            value={reasonId === undefined ? "" : String(reasonId)}
            placeholder={t("form.choose", "Choose…")}
            options={(reasons.data ?? [])
              .filter((reason) => reason.is_active)
              .map((reason) => ({ value: String(reason.id), label: reason.name }))}
            onChange={(event) => setReasonId(event.target.value === "" ? undefined : Number(event.target.value))}
          />
        </Field>
        <Field label={t("form.field.date", "Date")} htmlFor="adj-date">
          <Input id="adj-date" type="date" value={date} onChange={(event) => setDate(event.target.value)} />
        </Field>
        <Field label={t("form.field.reference", "Reference")} htmlFor="adj-reference" hint={t("stock.reference.hint", "A count sheet or slip number.")}>
          <Input id="adj-reference" value={reference} onChange={(event) => setReference(event.target.value)} />
        </Field>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Segmented
          value={mode}
          onChange={setMode}
          options={[
            { value: "DELTA", label: t("stock.mode.delta", "Add or remove") },
            { value: "COUNTED", label: t("stock.mode.counted", "Set to counted") },
          ]}
        />
        <span className="text-[12px] text-ink-muted">
          {mode === "DELTA"
            ? t("stock.mode.delta.hint", "Each quantity is posted as typed: 5 adds five, -2 removes two.")
            : t("stock.mode.counted.hint", "Type what is on the shelf. The difference is worked out against the balance when you post, not the one shown here.")}
        </span>
      </div>

      {/* The picker sits outside the scrolling box: inside it, its list of
          matches was clipped to the height of the table. */}
      <div className="rounded-md border border-line">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-surface-sunk text-left text-[11px] uppercase tracking-wide text-ink-faint">
              <tr>
                <th className="px-3 py-2">{t("column.part.number", "Number")}</th>
                <th className="px-3 py-2 text-right">{t("stock.on-hand", "On hand")}</th>
                <th className="px-3 py-2 text-right">
                  {mode === "DELTA" ? t("stock.delta", "Change") : t("stock.counted", "Counted")}
                </th>
                {mode === "COUNTED" && <th className="px-3 py-2 text-right">{t("stock.delta", "Change")}</th>}
                <th className="px-3 py-2">{t("form.field.note", "Note")}</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => {
                const balance = balanceOf(line.part_id);
                const quantity = parsed(line.quantity);
                const delta = mode === "COUNTED" && !Number.isNaN(quantity) ? quantity - balance : quantity;
                return (
                  <tr key={line.part_id} className="border-t border-line">
                    <td className="px-3 py-1.5">
                      <span className="font-medium">{line.number}</span>
                      <span className="ml-2 text-[11px] text-ink-faint">{line.name}</span>
                    </td>
                    <td className={"px-3 py-1.5 text-right tabular-nums " + (balance < 0 ? "text-risk" : "")}>
                      {current.isFetching ? "…" : `${formatNumber(balance)} ${line.unit ?? ""}`}
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <Input
                        type="number"
                        step="any"
                        value={line.quantity}
                        onChange={(event) => setLine(line.part_id, { quantity: event.target.value })}
                        className="ml-auto h-8 w-28 text-right"
                        invalid={line.quantity !== "" && isInvalid(line)}
                      />
                    </td>
                    {mode === "COUNTED" && (
                      <td
                        className={
                          "px-3 py-1.5 text-right tabular-nums " +
                          (Number.isNaN(delta) || delta === 0 ? "text-ink-faint" : delta > 0 ? "text-ok" : "text-risk")
                        }
                      >
                        {Number.isNaN(delta) ? "" : `${delta > 0 ? "+" : ""}${formatNumber(delta)}`}
                      </td>
                    )}
                    <td className="px-3 py-1.5">
                      <Input
                        value={line.note}
                        onChange={(event) => setLine(line.part_id, { note: event.target.value })}
                        className="h-8"
                      />
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <button
                        type="button"
                        aria-label={t("button.remove", "Remove")}
                        className="text-ink-faint hover:text-risk"
                        onClick={() => setLines((existing) => existing.filter((item) => item.part_id !== line.part_id))}
                      >
                        <Trash2 className="size-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="border-t border-line px-3 py-2">
          <PartPicker onPick={addPart} exclude={partIds} />
        </div>
      </div>

      <Field label={t("form.field.note", "Note")} htmlFor="adj-note">
        <Textarea id="adj-note" rows={2} value={note} onChange={(event) => setNote(event.target.value)} />
      </Field>

      <div className="flex items-center gap-3">
        <Button variant="primary" disabled={!ready} loading={post.isPending} onClick={() => post.mutate()}>
          {mode === "DELTA" ? t("stock.adjust.post", "Post adjustment") : t("stock.count.post", "Post count")}
        </Button>
        {lines.length > 0 && (
          <Badge variant="neutral" size="sm">
            {t("stock.lines", "{{n}} lines", { n: lines.length })}
          </Badge>
        )}
        {!canWrite && (
          <span className="text-[12px] text-ink-muted">
            {t("stock.read-only", "Your account can view stock but not adjust it.")}
          </span>
        )}
        {blocker && <span className="text-[12px] text-ink-muted">{blocker}</span>}
      </div>
    </div>
  );
}
